import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { AppStatusStore } from '../app-status.store';
import { SignOutStop } from './sign-out-stop';
import { WaitingChange } from './waiting-changes-store';
import { WaitingChangesService } from './waiting-changes.service';

/** What the changes page lists: the Unsaved changes shown this time, then the Waiting changes in the order made. */
export interface ChangesView {
  unsaved: WaitingChange[];
  waiting: WaitingChange[];
}

/** The page's words for what a change did. */
export const CHANGE_OP_TEXT: Record<WaitingChange['op'], string> = { add: 'Added', edit: 'Edited', delete: 'Deleted' };

// Spelt out rather than taken from the locale, which writes September as "Sept" in some browsers and "Sep" in others.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * When a change was made, as the person's own clock showed it: "Today, 2:05 pm", "Yesterday, 9:30 am" or
 * "3 Oct, 4:00 pm". The change keeps Zavo's time, so the phone's offset is taken back off; otherwise a
 * phone running behind would see a time it never read.
 */
export function madeWhen(change: Pick<WaitingChange, 'madeAt' | 'clockOffsetMs'>, now = Date.now()): string {
  const made = new Date(change.madeAt - (change.clockOffsetMs ?? 0));
  const h = made.getHours();
  const time = `${h % 12 === 0 ? 12 : h % 12}:${String(made.getMinutes()).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const day = new Date(made);
  day.setHours(0, 0, 0, 0);
  const daysAgo = Math.round((today.getTime() - day.getTime()) / 86_400_000);
  if (daysAgo === 0) return `Today, ${time}`;
  if (daysAgo === 1) return `Yesterday, ${time}`;
  return `${made.getDate()} ${MONTHS[made.getMonth()]}, ${time}`;
}

/**
 * The changes page's state: closed (null) or what it shows. A root service holding a signal, like
 * AppStatusStore, so the banner opens it and the sign-out stop rides on it without either knowing the page.
 *
 * An Unsaved change is shown once: opening the page marks every unseen one seen, and the page keeps showing
 * those until it closes. Unsaved changes stay in the kept list, so a later change to the same record saving
 * never takes an earlier notice away before the person has read it.
 */
@Injectable({ providedIn: 'root' })
export class ChangesPage {
  private readonly waiting = inject(WaitingChangesService);
  private readonly status = inject(AppStatusStore);
  private readonly stop = inject(SignOutStop);

  readonly view = signal<ChangesView | null>(null);
  /** The Unsaved changes shown since the page opened, by change id. */
  private shown = new Map<string, WaitingChange>();
  private openedByStop = false;

  constructor() {
    // A change sent, kept or turned down while the page is open shows at once.
    effect(() => {
      this.status.waiting();
      this.status.unsavedUnseen();
      untracked(() => { if (this.view()) void this.refresh(); });
    });
    // The stop lists what signing out would lose, so it opens the page; answering closes what it opened.
    effect(() => {
      const asking = this.stop.asking();
      untracked(() => {
        if (asking && !this.view()) {
          this.openedByStop = true;
          void this.open();
        } else if (!asking && this.openedByStop) {
          this.openedByStop = false;
          this.close();
        }
      });
    });
  }

  async open(): Promise<void> {
    this.shown = new Map();
    // Open at once, so the tap is answered before the disk is.
    if (!this.view()) this.view.set({ unsaved: [], waiting: [] });
    await this.refresh();
  }

  close(): void {
    this.openedByStop = false;
    this.view.set(null);
  }

  private async refresh(): Promise<void> {
    // Only the signed-in person's changes: one left by anyone else is never theirs to see.
    const list = (await this.waiting.list()).filter((c) => this.waiting.isMine(c));
    if (!this.view()) return;
    const fresh = list.filter((c) => c.unsaved && !c.unsaved.seen && !this.shown.has(c.changeId));
    for (const c of fresh) this.shown.set(c.changeId, c);
    this.view.set({ unsaved: [...this.shown.values()], waiting: list.filter((c) => !c.unsaved) });
    if (!fresh.length) return;
    const ids = new Set(fresh.map((c) => c.changeId));
    try {
      await this.waiting.edit((changes) => changes.map((c) =>
        ids.has(c.changeId) && c.unsaved ? { ...c, unsaved: { ...c.unsaved, seen: true } } : c));
    } catch (err) {
      // Not written: the notice shows again next time, which is the safe way round.
      console.warn('[changes-page] could not mark Unsaved changes seen', err);
    }
  }
}
